import { Component, OnDestroy, OnInit } from '@angular/core';
import { ConnectableObservable, Observable, of, Subscription } from 'rxjs';
import { UserService } from '../../../core/services/user.service';
import { catchError, concatMap } from 'rxjs/operators';
import { LoadingService } from '../../../common/layout/loading.service';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ConfigurationService } from '../../../core/services/configuration.service';
import { AppInitializerService } from '../../../core/services/app-initializer.service';
import { tap } from 'rxjs/internal/operators/tap';
import { combineLatest } from 'rxjs/internal/observable/combineLatest';
import { AuthService } from '../auth.service';
import { GoldfingerSocketService } from '../../../common/shared/goldfinger-socket.service';
import { NotificationService } from '../../../common/layout/notification.service';
import { GenericErrorHandlerService } from '../../../core/services/generic-error-handler.service';

@Component({
    selector: 'ns-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
    private subscriptions: Subscription = new Subscription();
    public errorMessage: string;
    public form: FormGroup = this.formBuilder.group({
        username: [null, [Validators.required, Validators.email]],
        password: [null, Validators.required]
    });

    public showQR = false;
    public qrData;

    constructor(
        private formBuilder: FormBuilder,
        private userService: UserService,
        private loadingService: LoadingService,
        private authService: AuthService,
        private configurationService: ConfigurationService,
        private appInitializerService: AppInitializerService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private goldfingerSocketService: GoldfingerSocketService,
        private notificationService: NotificationService,
        private genericErrorHandlerService: GenericErrorHandlerService
    ) {

        if (activatedRoute.snapshot.data) {
            this.qrData = activatedRoute.snapshot.data['qr'];

            if (this.qrData && this.qrData.qrCode) {
                this.showQR = true;

                this.manageQR();
            }
        }

    }

    ngOnInit() {
        this.onValueChange();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    public manageQR() {
        this.goldfingerSocketService.listen(this.qrData.uuid);
        this.goldfingerSocketService.on(this.qrData.uuid, 'authenticate', (data: {key: string}) => {
            this.notificationService.success('Logowanie do systemu...');
            this.onSubmit({
                goldfingerToken: data.key,
            });
        });
        this.goldfingerSocketService.on(this.qrData.uuid, 'error', (error) => {
            this.notificationService.error(error);
        });

    }

    onSubmit(userData?) {
        const login$: ConnectableObservable<any> = userData ? this.userService.login(userData) : this.userService.login(this.form.getRawValue());
        const configuration$: Observable<any> = this.configurationService.downloadConfiguration();
        const currentUser$: Observable<any> = this.userService.downloadCurrentUser();

        this.genericErrorHandlerService.showErrors(this.form);

        if (this.form.valid || userData) {
            const sub = login$.pipe(
                tap(() => this.loadingService.start()),
                catchError((error) => {
                    this.errorMessage = (error.error && error.error.data && error.error.data.error_message)
                        ? error.error.data.error_message : 'Wystąpił błąd podczas logowania do systemu';
                    this.loadingService.stop();
                    this.form.get('username').setErrors({});
                    this.form.get('password').setErrors({'errorMessage': ''});

                    this.notificationService.error(this.errorMessage);

                    this.loadingService.stop();
                    return of(null);
                }),
                concatMap(() => combineLatest(currentUser$)),
            ).subscribe(() => {
                this.loadingService.stop();
                this.userService.userHasLogged();
                const urlBeforeLogin = this.authService.urlBeforeLogin;
                const redirectUrl = urlBeforeLogin && urlBeforeLogin !== '/logowanie' ? urlBeforeLogin : '/'
                this.router.navigate([redirectUrl]);
            });
            this.subscriptions.add(sub);
        }
    }

    private onValueChange() {
        this.form.valueChanges.subscribe((value) => {
            if (this.errorMessage) {
                this.form.get('username').setErrors(null);
                this.form.get('password').setErrors(null);
                this.errorMessage = null;
            }
        });
    }

    goToForgotPassword() {
        this.router.navigate(['zapomnialem-haslo']);
    }
}
